import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Link } from "react-router-dom";
import "./Stripe.css";
import { useDispatch } from "react-redux";
import { charge } from "../../redux/actions/checkoutAction";
import { UncontrolledTooltip } from "reactstrap";
import { StripeKey } from "../../environments/environment";

const CARD_OPTIONS = {
  iconStyle: "solid",
  style: {
    base: {
      iconColor: "#000000",
      color: "#000000",
      fontWeight: 500,
      fontFamily: "'Montserrat', sans-serif",
      fontSize: "12px",
      fontSmoothing: "antialiased",
      ":-webkit-autofill": {
        color: "#fce883",
      },
      "::placeholder": {
        color: "#333333",
      },
    },
    invalid: {
      iconColor: "#eb1c26",
      color: "#eb1c26",
    },
  },
  hidePostalCode: true,
};

const CardField = ({ onChange }) => (
  <div className="FormRow">
    <label className="form-label">Card number</label>
    <CardElement options={CARD_OPTIONS} onChange={onChange} />
  </div>
);

const Field = ({
  label,
  id,
  type,
  placeholder,
  required,
  autoComplete,
  value,
  name,
  onChange,
}) => (
  <>
    <label htmlFor={id} className="form-label">
      {label}
    </label>
    <input
      className="form-control"
      id={id}
      type={type}
      placeholder={placeholder}
      required={required}
      autoComplete={autoComplete}
      value={value}
      name={name}
      onChange={onChange}
    />
  </>
);

const SubmitButton = ({ processing, error, children, disabled }) => (
  <button
    className={`submit-btn submit-primary mt-0 ${
      error ? "SubmitButton--error" : ""
    }`}
    type="submit"
    disabled={processing || disabled}
  >
    {processing ? "Processing..." : children}
  </button>
);

const ErrorMessage = ({ children }) => (
  <div className="ErrorMessage" role="alert">
    {children}
  </div>
);

const CheckoutForm = (props) => {
  const {isChargeStarted} = props;
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [billingDetails, setBillingDetails] = useState({
    customer_name: "",
    customer_email: "",
    name: "",
    vat_no: "",
    bill_to: "",
    address: {
      country: "",
      postal_code: "",
      line1: "",
      city: "",
      state: "",
    },
  });

  const dispatch = useDispatch();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    if (error) {
      elements.getElement("card").focus();
      return;
    }

    if (cardComplete) {
      setProcessing(true);
    }

    let newbillingDetails = { ...billingDetails };
    delete newbillingDetails.customer_name;
    delete newbillingDetails.customer_email;
    delete newbillingDetails.vat_no;
    delete newbillingDetails.bill_to;

    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
      billing_details: newbillingDetails,
    });

    setProcessing(false);

    if (payload.error) {
      setError(payload.error);
    } else {
      setPaymentMethod(payload.paymentMethod);
      const chargeDetails = {
        payment_method: payload.paymentMethod.id,
        coupon: props.coupon,
        plan_id: props.planId,
        plan_description: props.plan_description,
        billing_detail: {
          name: billingDetails.customer_name,
          email: billingDetails.customer_email,
          bill_to: billingDetails.bill_to,
          country: billingDetails.address.country,
          city: billingDetails.address.city,
          line1: billingDetails.address.line1,
          state: billingDetails.address.state,
          zip_code: billingDetails.address.postal_code,
          vat_no: billingDetails.vat_no,
        },
      };
      dispatch(charge(chargeDetails));
    }
  };

  const _setBillingDetails = (e) => {
    e.persist();
    setBillingDetails((prevState) => {
      prevState.address[e.target.name] = e.target.value;
      return {
        ...prevState,
      };
    });
  };

  return (
    <form className="form" onSubmit={handleSubmit}>
      <h3 className="rs_h4">Customer Details</h3>
      <div className="form-group mb-0">
        <Field
          label="Customer name"
          id="customer_name"
          type="text"
          required
          autoComplete="customer_name"
          value={billingDetails.customer_name}
          onChange={(e) => {
            setBillingDetails({
              ...billingDetails,
              customer_name: e.target.value,
            });
          }}
        />
      </div>
      <div className="form-group mb-5">
        <Field
          label="Customer email"
          id="customer_email"
          type="email"
          required
          autoComplete="customer_email"
          value={billingDetails.customer_email}
          onChange={(e) => {
            setBillingDetails({
              ...billingDetails,
              customer_email: e.target.value,
            });
          }}
        />
      </div>
      <h3 className="rs_h4">Card Details</h3>
      <div className="form-group mb-0">
        <Field
          label="Name on card"
          id="name"
          type="text"
          required
          autoComplete="name"
          value={billingDetails.name}
          onChange={(e) => {
            setBillingDetails({ ...billingDetails, name: e.target.value });
          }}
        />
      </div>
      <div className="form-group mb-0">
        <CardField
          onChange={(e) => {
            setError(e.error);
            setCardComplete(e.complete);
          }}
        />
      </div>
      <div className="form-group mb-0">
        <Field
          label="Bill To"
          id="bill_to"
          type="text"
          required
          autoComplete="bill_to"
          value={billingDetails.bill_to}
          onChange={(e) => {
            setBillingDetails({ ...billingDetails, bill_to: e.target.value });
          }}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Country</label>
        <select
          className="form-control select_arrow_gray"
          name="country"
          value={billingDetails.address.country}
          onChange={_setBillingDetails}
          required
        >
          <option>Select an Option</option>
          <option value="AF">Afghanistan</option>
          <option value="AX">Aland Islands</option>
          <option value="AL">Albania</option>
          <option value="DZ">Algeria</option>
          <option value="AS">American Samoa</option>
          <option value="AD">Andorra</option>
          <option value="AO">Angola</option>
          <option value="AI">Anguilla</option>
          <option value="AQ">Antarctica</option>
          <option value="AG">Antigua and Barbuda</option>
          <option value="AR">Argentina</option>
          <option value="AM">Armenia</option>
          <option value="AW">Aruba</option>
          <option value="AU">Australia</option>
          <option value="AT">Austria</option>
          <option value="AZ">Azerbaijan</option>
          <option value="BS">Bahamas</option>
          <option value="BH">Bahrain</option>
          <option value="BD">Bangladesh</option>
          <option value="BB">Barbados</option>
          <option value="BY">Belarus</option>
          <option value="BE">Belgium</option>
          <option value="BZ">Belize</option>
          <option value="BJ">Benin</option>
          <option value="BM">Bermuda</option>
          <option value="BT">Bhutan</option>
          <option value="BO">Bolivia</option>
          <option value="BA">Bosnia and Herzegovina</option>
          <option value="BW">Botswana</option>
          <option value="BV">Bouvet Island</option>
          <option value="BR">Brazil</option>
          <option value="IO">British Indian Ocean Territory</option>
          <option value="BN">Brunei Darussalam</option>
          <option value="BG">Bulgaria</option>
          <option value="BF">Burkina Faso</option>
          <option value="BI">Burundi</option>
          <option value="KH">Cambodia</option>
          <option value="CM">Cameroon</option>
          <option value="CA">Canada</option>
          <option value="CV">Cape Verde</option>
          <option value="KY">Cayman Islands</option>
          <option value="CF">Central African Republic</option>
          <option value="TD">Chad</option>
          <option value="CL">Chile</option>
          <option value="CN">China</option>
          <option value="CX">Christmas Island</option>
          <option value="CC">Cocos (Keeling) Islands</option>
          <option value="CO">Colombia</option>
          <option value="KM">Comoros</option>
          <option value="CG">Congo</option>
          <option value="CD">Congo, The Democratic Republic of the</option>
          <option value="CK">Cook Islands</option>
          <option value="CR">Costa Rica</option>
          <option value="CI">Cote D'Ivoire</option>
          <option value="HR">Croatia</option>
          <option value="CU">Cuba</option>
          <option value="CY">Cyprus</option>
          <option value="CZ">Czech Republic</option>
          <option value="DK">Denmark</option>
          <option value="DJ">Djibouti</option>
          <option value="DM">Dominica</option>
          <option value="DO">Dominican Republic</option>
          <option value="EC">Ecuador</option>
          <option value="EG">Egypt</option>
          <option value="SV">El Salvador</option>
          <option value="GQ">Equatorial Guinea</option>
          <option value="ER">Eritrea</option>
          <option value="EE">Estonia</option>
          <option value="ET">Ethiopia</option>
          <option value="FK">Falkland Islands (Malvinas)</option>
          <option value="FO">Faroe Islands</option>
          <option value="FJ">Fiji</option>
          <option value="FI">Finland</option>
          <option value="FR">France</option>
          <option value="GF">French Guiana</option>
          <option value="PF">French Polynesia</option>
          <option value="TF">French Southern Territories</option>
          <option value="GA">Gabon</option>
          <option value="GM">Gambia</option>
          <option value="GE">Georgia</option>
          <option value="DE">Germany</option>
          <option value="GH">Ghana</option>
          <option value="GI">Gibraltar</option>
          <option value="GR">Greece</option>
          <option value="GL">Greenland</option>
          <option value="GD">Grenada</option>
          <option value="GP">Guadeloupe</option>
          <option value="GU">Guam</option>
          <option value="GT">Guatemala</option>
          <option value="GG">Guernsey</option>
          <option value="GN">Guinea</option>
          <option value="GW">Guinea-Bissau</option>
          <option value="GY">Guyana</option>
          <option value="HT">Haiti</option>
          <option value="HM">Heard Island and Mcdonald Islands</option>
          <option value="VA">Holy See (Vatican City State)</option>
          <option value="HN">Honduras</option>
          <option value="HK">Hong Kong</option>
          <option value="HU">Hungary</option>
          <option value="IS">Iceland</option>
          <option value="IN">India</option>
          <option value="ID">Indonesia</option>
          <option value="IR">Iran, Islamic Republic Of</option>
          <option value="IQ">Iraq</option>
          <option value="IE">Ireland</option>
          <option value="IM">Isle of Man</option>
          <option value="IL">Israel</option>
          <option value="IT">Italy</option>
          <option value="JM">Jamaica</option>
          <option value="JP">Japan</option>
          <option value="JE">Jersey</option>
          <option value="JO">Jordan</option>
          <option value="KZ">Kazakhstan</option>
          <option value="KE">Kenya</option>
          <option value="KI">Kiribati</option>
          <option value="KP">Korea, Democratic People'S Republic of</option>
          <option value="KR">Korea, Republic of</option>
          <option value="KW">Kuwait</option>
          <option value="KG">Kyrgyzstan</option>
          <option value="LA">Lao People'S Democratic Republic</option>
          <option value="LV">Latvia</option>
          <option value="LB">Lebanon</option>
          <option value="LS">Lesotho</option>
          <option value="LR">Liberia</option>
          <option value="LY">Libyan Arab Jamahiriya</option>
          <option value="LI">Liechtenstein</option>
          <option value="LT">Lithuania</option>
          <option value="LU">Luxembourg</option>
          <option value="MO">Macao</option>
          <option value="MK">Macedonia, The Former Yugoslav Republic of</option>
          <option value="MG">Madagascar</option>
          <option value="MW">Malawi</option>
          <option value="MY">Malaysia</option>
          <option value="MV">Maldives</option>
          <option value="ML">Mali</option>
          <option value="MT">Malta</option>
          <option value="MH">Marshall Islands</option>
          <option value="MQ">Martinique</option>
          <option value="MR">Mauritania</option>
          <option value="MU">Mauritius</option>
          <option value="YT">Mayotte</option>
          <option value="MX">Mexico</option>
          <option value="FM">Micronesia, Federated States of</option>
          <option value="MD">Moldova, Republic of</option>
          <option value="MC">Monaco</option>
          <option value="MN">Mongolia</option>
          <option value="ME">Montenegro</option>
          <option value="MS">Montserrat</option>
          <option value="MA">Morocco</option>
          <option value="MZ">Mozambique</option>
          <option value="MM">Myanmar</option>
          <option value="NA">Namibia</option>
          <option value="NR">Nauru</option>
          <option value="NP">Nepal</option>
          <option value="NL">Netherlands</option>
          <option value="AN">Netherlands Antilles</option>
          <option value="NC">New Caledonia</option>
          <option value="NZ">New Zealand</option>
          <option value="NI">Nicaragua</option>
          <option value="NE">Niger</option>
          <option value="NG">Nigeria</option>
          <option value="NU">Niue</option>
          <option value="NF">Norfolk Island</option>
          <option value="MP">Northern Mariana Islands</option>
          <option value="NO">Norway</option>
          <option value="OM">Oman</option>
          <option value="PK">Pakistan</option>
          <option value="PW">Palau</option>
          <option value="PS">Palestinian Territory, Occupied</option>
          <option value="PA">Panama</option>
          <option value="PG">Papua New Guinea</option>
          <option value="PY">Paraguay</option>
          <option value="PE">Peru</option>
          <option value="PH">Philippines</option>
          <option value="PN">Pitcairn</option>
          <option value="PL">Poland</option>
          <option value="PT">Portugal</option>
          <option value="PR">Puerto Rico</option>
          <option value="QA">Qatar</option>
          <option value="RE">Reunion</option>
          <option value="RO">Romania</option>
          <option value="RU">Russian Federation</option>
          <option value="RW">RWANDA</option>
          <option value="SH">Saint Helena</option>
          <option value="KN">Saint Kitts and Nevis</option>
          <option value="LC">Saint Lucia</option>
          <option value="PM">Saint Pierre and Miquelon</option>
          <option value="VC">Saint Vincent and the Grenadines</option>
          <option value="WS">Samoa</option>
          <option value="SM">San Marino</option>
          <option value="ST">Sao Tome and Principe</option>
          <option value="SA">Saudi Arabia</option>
          <option value="SN">Senegal</option>
          <option value="RS">Serbia</option>
          <option value="SC">Seychelles</option>
          <option value="SL">Sierra Leone</option>
          <option value="SG">Singapore</option>
          <option value="SK">Slovakia</option>
          <option value="SI">Slovenia</option>
          <option value="SB">Solomon Islands</option>
          <option value="SO">Somalia</option>
          <option value="ZA">South Africa</option>
          <option value="GS">
            South Georgia and the South Sandwich Islands
          </option>
          <option value="ES">Spain</option>
          <option value="LK">Sri Lanka</option>
          <option value="SD">Sudan</option>
          <option value="SR">Suriname</option>
          <option value="SJ">Svalbard and Jan Mayen</option>
          <option value="SZ">Swaziland</option>
          <option value="SE">Sweden</option>
          <option value="CH">Switzerland</option>
          <option value="SY">Syrian Arab Republic</option>
          <option value="TW">Taiwan, Province of China</option>
          <option value="TJ">Tajikistan</option>
          <option value="TZ">Tanzania, United Republic of</option>
          <option value="TH">Thailand</option>
          <option value="TL">Timor-Leste</option>
          <option value="TG">Togo</option>
          <option value="TK">Tokelau</option>
          <option value="TO">Tonga</option>
          <option value="TT">Trinidad and Tobago</option>
          <option value="TN">Tunisia</option>
          <option value="TR">Turkey</option>
          <option value="TM">Turkmenistan</option>
          <option value="TC">Turks and Caicos Islands</option>
          <option value="TV">Tuvalu</option>
          <option value="UG">Uganda</option>
          <option value="UA">Ukraine</option>
          <option value="AE">United Arab Emirates</option>
          <option value="GB">United Kingdom</option>
          <option value="US">United States of America</option>
          <option value="UM">United States Minor Outlying Islands</option>
          <option value="UY">Uruguay</option>
          <option value="UZ">Uzbekistan</option>
          <option value="VU">Vanuatu</option>
          <option value="VE">Venezuela</option>
          <option value="VN">Viet Nam</option>
          <option value="VG">Virgin Islands, British</option>
          <option value="VI">Virgin Islands, U.S.</option>
          <option value="WF">Wallis and Futuna</option>
          <option value="EH">Western Sahara</option>
          <option value="YE">Yemen</option>
          <option value="ZM">Zambia</option>
          <option value="ZW">Zimbabwe</option>
        </select>
      </div>
      <div className="form-group mb-0">
        <Field
          label="Address"
          id="line1"
          type="text"
          required
          autoComplete="line1"
          name="line1"
          value={billingDetails.address.line1}
          onChange={_setBillingDetails}
        />
      </div>
      <div className="row gutters-5">
        <div className="col-6">
          <div className="form-group mb-0">
            <Field
              label="City"
              id="city"
              type="text"
              required
              autoComplete="city"
              name="city"
              value={billingDetails.address.city}
              onChange={_setBillingDetails}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="form-group mb-0">
            <Field
              label="State"
              id="state"
              type="text"
              required
              autoComplete="state"
              name="state"
              value={billingDetails.address.state}
              onChange={_setBillingDetails}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="form-group mb-0">
            <Field
              label="Postal code"
              id="postal_code"
              type="number"
              required
              autoComplete="postal_code"
              name="postal_code"
              value={billingDetails.address.postal_code}
              onChange={_setBillingDetails}
            />
          </div>
        </div>
        <div className="col-6">
          <div className="form-group mb-0">
            <label htmlFor="vat_no" className="form-label">
              Vat
              <span id="Tooltip-vat">
                ?
              </span>
              <UncontrolledTooltip
                placement="bottom"
                target="Tooltip-vat"
                autohide={false}
              >
                A value added tax (VAT) identification number is used in many
                countries, including the European Union, for value added tax
                purposes. It is typically not used in the US or Canada
              </UncontrolledTooltip>
            </label>
            <input
              className="form-control"
              id="vat_no"
              type="text"
              autoComplete="vat_no"
              name="vat_no"
              value={billingDetails.vat_no}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  vat_no: e.target.value,
                });
              }}
            ></input>
            {/* <Field
              label="Vat"
              id="vat_no"
              type="text"
              autoComplete="vat_no"
              name="vat_no"
              value={billingDetails.vat_no}
              onChange={(e) => {
                setBillingDetails({ ...billingDetails, vat_no: e.target.value });
              }}
            /> */}
          </div>
        </div>
      </div>

      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      <div className="form-group mt-5">
        {!isChargeStarted && <SubmitButton processing={processing} error={error} disabled={!stripe}>
          Complete Payment
        </SubmitButton>}
        {
          isChargeStarted && <button type="button" className="submit-btn submit-primary mt-0" disabled>Almost Done !...</button>
        }
        <Link to="/plans" className="submit-btn submit-white">
          Cancel
        </Link>
      </div>
    </form>
  );
};

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc:
        "https://fonts.googleapis.com/css?family=Montserrat:300,400,500,600,700,900|Montserrat:300,400,700",
    },
  ],
};

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(StripeKey);

const StripeForm = (props) => {
  return (
    <div className="clearfix profileArea">
      <Elements stripe={stripePromise} options={ELEMENTS_OPTIONS}>
        <CheckoutForm planId={props.planId} coupon={props.coupon} isChargeStarted={props.isChargeStarted} plan_description={props.plan_description} />
      </Elements>
    </div>
  );
};

export default StripeForm;
